import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { prepareQuestions, prepareCategoryLabel } from '../../shared/lib';
import FormField from '../FormField';
import FormHeading from '../FormHeading';
import InfoIcon from 'assets/images/shared/info.svg';
import Tooltip from 'shared/components/Tooltip';
import BravelyData from 'utils/data';

import styles from './styles';
import { camelize } from 'humps';
import { Trans, useTranslation } from 'react-i18next';

const needsAndDemographyEnabled = BravelyData('needsAndDemographyEnabled');
export default function FormFields({
  questions,
  hidden,
  required,
  conditions,
  context,
  features,
  dispatch,
  submission,
  disableFields,
  ...props
}) {
  const { t } = useTranslation();
  const [focusField, setFocusField] = useState();
  const prevSubmitCountRef = useRef(props.submitCount);
  const data = disableFields
    ? BravelyData('formRenderer')?.submission?.data
    : undefined;

  const preparedQuestions = prepareQuestions({
    questions,
    hidden,
    features,
    context,
    data,
  });

  // Focus the first field with an error
  useEffect(() => {
    if (prevSubmitCountRef.current !== props.submitCount && !props.isValid) {
      setFocusField(Object.keys(props.errors)[0] || null);
    }
    prevSubmitCountRef.current = props.submitCount;
    return () => setTimeout(setFocusField, 500, null); // Clear the field focus value
  }, [props.submitCount, props.isValid, props.errors]);

  const renderCategoryLabel = (label) => {
    if (
      label ===
      'I grant permission for my anonymous written feedback to be used by...'
    ) {
      return (
        <Trans t={t}>
          <span className={styles.optIn}>
            I grant permission for my <strong>anonymous</strong> written
            feedback to be used by...
          </span>
        </Trans>
      );
    }
    return t(prepareCategoryLabel(label));
  };

  const renderFormFields = ({
    questions,
    focusField,
    context,
    required,
    hidden,
    props,
  }) =>
    questions.map((question, index) => {
      return (
        <FormField
          key={question.name}
          question={question}
          context={context}
          required={required[question.name]}
          hidden={!disableFields && hidden[question.name]}
          error={props.errors[question.name]}
          touched={props.touched[question.name]}
          value={props.values[question.name]}
          focus={focusField === question.name}
          submitCount={props.submitCount}
          disabled={disableFields}
          isValid={props.isValid}
        />
      );
    });

  const renderFormFieldsByCategory = ({
    questions,
    focusField,
    context,
    required,
    hidden,
    props,
  }) =>
    Object.keys(questions).map((category) => {
      const showCategoryLabel =
        category && questions[category].label !== category;

      const descriptionPosition =
        questions[category].description && questions[category].position;
      return (
        !!questions[category].questions.length && (
          <div key={category} className={styles.section}>
            {showCategoryLabel && (
              <div
                className={cx({
                  [styles.reverseColumn]: descriptionPosition === 'top',
                })}
              >
                <FormHeading
                  subCategory={questions[category].subCategory}
                  className={cx(styles.category, {
                    [styles.subCategory]: questions[category].subCategory,
                    [styles.numberedFields]: !needsAndDemographyEnabled,
                  })}
                >
                  {renderCategoryLabel(questions[category].label)}
                  {questions[category].tooltip && (
                    <Tooltip
                      icon={InfoIcon}
                      hover={true}
                      description={questions[category].tooltip}
                    />
                  )}
                </FormHeading>

                {questions[category].description && (
                  <span
                    className={styles.categoryDescription}
                    dangerouslySetInnerHTML={{
                      __html: t(
                        prepareCategoryLabel(questions[category].description)
                      ),
                    }}
                  ></span>
                )}
              </div>
            )}
            {renderFormFields({
              questions: questions[category].questions,
              focusField,
              context,
              required,
              hidden,
              props,
            })}
          </div>
        )
      );
    });

  return (
    <div
      className={cx(
        styles.fields,
        styles[camelize(`${submission.name}Fields`)]
      )}
    >
      {features.categories &&
        renderFormFieldsByCategory({
          questions: preparedQuestions,
          focusField,
          context,
          required,
          hidden,
          props,
        })}
      {!features.categories &&
        renderFormFields({
          questions: preparedQuestions,
          focusField,
          context,
          required,
          hidden,
          props,
        })}
    </div>
  );
}

FormFields.propTypes = {
  questions: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  features: PropTypes.object.isRequired,
  submission: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};
